import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { swatchParts, taka } from './shopUtils';

export const Swatch = ({ c, active, onClick }) => {
  const { language } = useLanguage();
  const parts = swatchParts(c);
  const label = c.split('/').map((x, i) => `${x.trim()} · ${parts[i][1]}`).join(' / ');
  const bg = parts.length > 1 ? `linear-gradient(90deg, ${parts[0][0]} 50%, ${parts[1][0]} 50%)` : parts[0][0];
  return (
    <button type="button" onClick={onClick} title={label} aria-label={label} aria-pressed={active}
      className={`group relative h-6 w-6 rounded-full border border-[#E5E7EB] transition-shadow focus:outline-none ${active ? 'ring-2 ring-[#0A5C2C] ring-offset-2' : 'hover:ring-1 hover:ring-[#9CA3AF] hover:ring-offset-1 focus-visible:ring-2 focus-visible:ring-[#0A5C2C]'}`}
      style={{ background: bg }} data-testid={`swatch-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <span className="pointer-events-none absolute -top-9 left-1/2 z-10 hidden -translate-x-1/2 whitespace-nowrap rounded bg-[#0A0A0A] px-2 py-1 text-[11px] text-white group-hover:block group-focus-visible:block">{language === 'bn' ? label.split(' / ').map((x) => x.split(' · ').reverse().join(' · ')).join(' / ') : label}</span>
    </button>
  );
};

export const ContentsList = ({ p }) => {
  const { t } = useLanguage();
  const row = (p.spec || []).find(([k]) => k === 'Contents');
  if (!row) return null;
  return (
    <div className="mt-6 rounded-lg border border-[#E5E7EB] p-4" data-testid="set-contents">
      <p className="mb-2 text-sm font-semibold">{t({ en: "What's in the box", bn: 'বাক্সে যা আছে' })}</p>
      <ul className="space-y-1.5 text-sm">{row[1].split('·').map((x) => x.trim()).filter(Boolean).map((x) => <li key={x} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0E9A49]" />{x.charAt(0).toUpperCase() + x.slice(1)}</li>)}</ul>
    </div>
  );
};

export const SetFields = ({ p, extras, setExtras }) => {
  const { t, language } = useLanguage();
  const input = 'mt-1 w-full rounded-md border border-[#E5E7EB] px-3 py-2 text-sm';
  return (
    <div className="mt-5 space-y-3" data-testid="set-fields">
      {p.personalise && (
        <label className="block text-sm font-medium">{t({ en: 'Name to engrave or deboss', bn: 'খোদাই বা ডিবস করার নাম' })} <span className="font-normal text-[#6B7280]">({t({ en: 'optional', bn: 'ঐচ্ছিক' })}{p.personalise_price ? ` · +${taka(p.personalise_price, language)}` : ` · ${t({ en: 'included', bn: 'অন্তর্ভুক্ত' })}`})</span>
          <input maxLength={40} value={extras.personalisation} onChange={(e) => setExtras({ ...extras, personalisation: e.target.value })} className={input} data-testid="set-personalisation" />
          <span className="text-xs text-[#6B7280]">{extras.personalisation.length}/40</span>
        </label>
      )}
      <label className="block text-sm font-medium">{t({ en: p.baby ? 'Message for the welcome card' : 'Gift message for the compliments card', bn: p.baby ? 'স্বাগতম কার্ডের বার্তা' : 'শুভেচ্ছা কার্ডের বার্তা' })} <span className="font-normal text-[#6B7280]">({t({ en: 'optional', bn: 'ঐচ্ছিক' })})</span>
        <textarea maxLength={200} rows={2} value={extras.gift_message} onChange={(e) => setExtras({ ...extras, gift_message: e.target.value })} className={input} data-testid="set-gift-message" />
        <span className="text-xs text-[#6B7280]">{extras.gift_message.length}/200</span>
      </label>
    </div>
  );
};

export const GiftActions = ({ p }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 space-y-3">
      <button type="button" onClick={() => navigate(`/shop/bulk-orders?item=${encodeURIComponent(p.name.en)}&cat=gifts&qty=10`)} className="shop-btn w-full border border-[#0A5C2C] text-[#0A5C2C] hover:bg-[#ECFDF3]" data-testid="gift-quote-btn">{t({ en: 'Request a quote for 10+ sets', bn: '১০+ সেটের জন্য কোটেশন চান' })}</button>
      <div className="rounded-lg border border-[#E5E7EB]" data-testid="gifting-guidance">
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold" data-testid="gifting-guidance-toggle">
          <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#0A5C2C]" />{t({ en: 'Gifting guidance', bn: 'উপহার নির্দেশিকা' })}</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && <p className="px-4 pb-4 text-sm text-[#4B5563]" data-testid="gifting-guidance-text">{t({ en: 'Gift sets are courtesy gifts. Public officials, ministers and diplomats follow their own gift rules — please check before presenting. Gift sets and baby kits are not distributed to voters during an election schedule.', bn: 'উপহার সেটগুলো সৌজন্যমূলক উপহার। সরকারি কর্মকর্তা, মন্ত্রী ও কূটনীতিকরা নিজস্ব উপহার বিধি মেনে চলেন — উপহার দেওয়ার আগে অনুগ্রহ করে যাচাই করুন। নির্বাচনী তফসিল চলাকালে ভোটারদের মধ্যে উপহার সেট ও শিশু কিট বিতরণ করা হয় না।' })}</p>}
      </div>
    </div>
  );
};

export const WelcomeCardFields = ({ line, value, onChange }) => {
  const { t, language } = useLanguage();
  const input = 'mt-1 w-full rounded-md border border-[#E5E7EB] px-3 py-2 text-sm';
  return (
    <div className="rounded-lg border border-[#E5E7EB] p-4" data-testid={`welcome-card-${line.variant_sku}`}>
      <p className="text-sm font-semibold">{t({ en: 'Welcome card', bn: 'স্বাগতম কার্ড' })} — {language === 'bn' ? line.name.bn || line.name.en : line.name.en} <span className="font-normal text-[#6B7280]">({t({ en: 'optional', bn: 'ঐচ্ছিক' })})</span></p>
      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        <label className="text-sm">{t({ en: "Baby's name", bn: 'শিশুর নাম' })}<input maxLength={60} value={value.baby_name || ''} onChange={(e) => onChange({ ...value, baby_name: e.target.value })} className={input} data-testid={`welcome-baby-name-${line.variant_sku}`} /></label>
        <label className="text-sm">{t({ en: 'Date of birth', bn: 'জন্মতারিখ' })}<input type="date" value={value.baby_dob || ''} onChange={(e) => onChange({ ...value, baby_dob: e.target.value })} className={input} data-testid={`welcome-baby-dob-${line.variant_sku}`} /></label>
      </div>
      <p className="mt-2 text-xs text-[#6B7280]">{t({ en: 'Kept private — printed on the card only and deleted 90 days after delivery.', bn: 'গোপন রাখা হয় — শুধু কার্ডে ছাপা হয় এবং ডেলিভারির ৯০ দিন পর মুছে ফেলা হয়।' })}</p>
    </div>
  );
};
